/**
 * Team Inbox — "Add to existing" (link a triage item to an EXISTING contact).
 *
 * The triage strip (Email + WhatsApp) lets staff Create Lead / Create Contact /
 * Archive / Hold. When the sender is ALREADY a contact in the system, none of
 * those fit — you'd create a duplicate. "Add to existing" closes that gap: pick
 * the existing contact, and this module ATTACHES the message's channel identity
 * to that contact's record so:
 *
 *   • email  → the sender email is written onto the contact (top-level `email`
 *              if empty + `linked_google_emails[]` always) so the server email
 *              router auto-matches FUTURE inbound to that contact (no re-triage)
 *              — see avbp_brevo_sync.routeInboundEmail (`where('email','==')`
 *              then `linked_google_emails array-contains`). Mirrored into the
 *              Person-Hub `emails[]` for the Contact UI.
 *   • whatsapp → the phone is written onto the contact (`phones[]` with
 *              whatsApp:true + top-level `phone` if empty) so the client-side
 *              resolveContactByWhatsApp AND the server resolveKnownClient
 *              (`where('phone','==', variant)`) both match future inbounds.
 *
 * This is a LINK/MERGE, not a rewrite: nothing already on the contact is
 * overwritten — identities are added, never replaced; the raw triage verbatim
 * is untouched.
 *
 * Everything here is PURE (no Firebase import) so the filter + merge contracts
 * are unit-tested with the Node test runner (test/_tsload.cjs). The impure
 * Firestore writes live in lib/triageStore.ts and call these builders.
 */
import type { LiveClient } from '../../../lib/projectStore';
import type { Lead } from '../../../lib/leads';

/* ───────────────────────── phone / email normalisation ───────────────────────── */

/**
 * PURE — normalise a phone for comparison: strip spaces/dashes/brackets and the
 * +91 / 091 / leading-0 India prefixes. MUST mirror `_normWaPhone` in
 * projectStore so the picker's "already linked" detection agrees with the live
 * WA resolver.
 */
export function normPhone(p: string): string {
  let n = (p || '').replace(/[\s\-().]/g, '');
  n = n.replace(/^\+91/, '').replace(/^091/, '');
  n = n.replace(/^0/, '');
  return n;
}

/** PURE — lower-cased, trimmed email for comparison. */
export function normEmail(e: string): string {
  return (e || '').trim().toLowerCase();
}

/* ───────────────────────── picker search ───────────────────────── */

/**
 * PURE — does this contact already carry the given phone (top-level `phone` or
 * any `phones[]` entry)? Used to show a "already linked" hint in the picker.
 */
export function contactHasPhone(c: LiveClient, phone: string): boolean {
  const target = normPhone(phone);
  if (!target) return false;
  if (c.phone && normPhone(c.phone) === target) return true;
  for (const e of c.phones ?? []) {
    if (normPhone(e.number) === target) return true;
  }
  return false;
}

/**
 * PURE — does this contact already carry the given email (top-level `email` or
 * any `emails[]` entry)?
 */
export function contactHasEmail(c: LiveClient, email: string): boolean {
  const target = normEmail(email);
  if (!target) return false;
  if (c.email && normEmail(c.email) === target) return true;
  for (const e of c.emails ?? []) {
    if (normEmail(e.address) === target) return true;
  }
  return false;
}

/**
 * PURE — searchable haystack for a contact: name + clientNo + every phone
 * (normalised AND raw) + every email. Lower-cased once.
 */
function haystack(c: LiveClient): string {
  const parts: string[] = [c.name || '', c.clientNo || '', c.location || ''];
  if (c.phone) {
    parts.push(c.phone, normPhone(c.phone));
  }
  for (const p of c.phones ?? []) {
    parts.push(p.number, normPhone(p.number));
  }
  if (c.email) parts.push(c.email);
  for (const e of c.emails ?? []) parts.push(e.address);
  return parts.join(' ').toLowerCase();
}

/**
 * PURE — filter the already-loaded contacts for the picker.
 *
 *  - Trashed contacts are never offered (they're hidden everywhere else too).
 *  - An empty query returns the first `limit` active contacts (recent-first,
 *    the order `listenClients` already provides) so the picker is useful on
 *    open without typing.
 *  - A query matches across name / C##### / phone (normalised, so "9179…"
 *    matches "+91 91793…") / email. Multi-token queries AND across tokens.
 *  - Results are capped at `limit` to keep the list light.
 */
export function filterContactsForPicker(
  contacts: LiveClient[],
  rawQuery: string,
  limit = 30,
): LiveClient[] {
  const active = (contacts || []).filter((c) => !c.trashed);
  const q = (rawQuery || '').trim().toLowerCase();
  if (!q) return active.slice(0, limit);
  const tokens = q.split(/\s+/).filter(Boolean);
  const normQuery = normPhone(rawQuery);
  const matched = active.filter((c) => {
    const hay = haystack(c);
    // every word token must appear somewhere in the haystack…
    const tokensOk = tokens.every((t) => hay.includes(t));
    // …OR the whole query, treated as a phone, matches a normalised number.
    const phoneOk = normQuery.length >= 4 && hay.includes(normQuery);
    return tokensOk || phoneOk;
  });
  return matched.slice(0, limit);
}

/* ───────────────────────── leads picker (Add to existing → Leads tab) ─────────────────────────
 *
 * "Add to existing" now searches BOTH contacts AND leads (two-tab picker). A
 * Lead carries its own identity (name/phone/email/location) on the pure model;
 * these helpers mirror the contact-side filter so the Leads tab searches the
 * same way and shows an "already linked" hint when a lead already carries this
 * exact channel identity.
 * ───────────────────────────────────────────────────────────────────────────────── */

/** PURE — does this lead already carry the given phone? */
export function leadHasPhone(l: Lead, phone: string): boolean {
  const target = normPhone(phone);
  if (!target) return false;
  return !!l.phone && normPhone(l.phone) === target;
}

/** PURE — does this lead already carry the given email? */
export function leadHasEmail(l: Lead, email: string): boolean {
  const target = normEmail(email);
  if (!target) return false;
  return !!l.email && normEmail(l.email) === target;
}

/** PURE — searchable haystack for a lead: name + id + phone (raw+norm) + email + location. */
function leadHaystack(l: Lead): string {
  const parts: string[] = [l.name || '', l.id || '', l.location || ''];
  if (l.phone) parts.push(l.phone, normPhone(l.phone));
  if (l.email) parts.push(l.email);
  return parts.join(' ').toLowerCase();
}

/**
 * PURE — filter the already-loaded leads for the picker's Leads tab. Mirrors
 * `filterContactsForPicker`: trashed leads never offered; empty query returns
 * the first `limit` (the order listenLeads provides — newest first); a query
 * matches across name / id / phone (normalised) / email / location; capped.
 */
export function filterLeadsForPicker(
  leads: Lead[],
  rawQuery: string,
  limit = 30,
): Lead[] {
  const active = (leads || []).filter((l) => !l.trashed);
  const q = (rawQuery || '').trim().toLowerCase();
  if (!q) return active.slice(0, limit);
  const tokens = q.split(/\s+/).filter(Boolean);
  const normQuery = normPhone(rawQuery);
  const matched = active.filter((l) => {
    const hay = leadHaystack(l);
    const tokensOk = tokens.every((t) => hay.includes(t));
    const phoneOk = normQuery.length >= 4 && hay.includes(normQuery);
    return tokensOk || phoneOk;
  });
  return matched.slice(0, limit);
}

/* ───────────────────────── create-from-form payloads (item 2) ─────────────────────────
 *
 * The old triage "Create Contact/Lead" dumped the phone number into BOTH the
 * name and the phone (a live bug). The redesign opens a real FORM (Name blank +
 * placeholder, Phone/Email pre-filled + editable). These PURE builders normalise
 * + validate the typed form values into the shape the impure writers consume, so
 * the name is exactly what the staff typed and the channel identity is the
 * (editable) field — never the number-as-name default.
 * ───────────────────────────────────────────────────────────────────────────────── */

export interface CreateForm {
  name: string;
  phone: string;
  email: string;
}

export interface CreateFormResult {
  name: string;
  phone: string;
  email: string;
  /** false → Name is blank; the form must block Save and show the required hint. */
  valid: boolean;
}

/**
 * PURE — normalise + validate a Create-Contact / Create-Lead form. Trims all
 * fields; `valid` is false when the name is blank (the form requires a typed
 * name — fixing the number-as-name bug). The phone/email pass through trimmed
 * and editable (pre-filled from the sender, but the staff may correct them).
 */
export function buildCreateFromForm(form: CreateForm): CreateFormResult {
  const name = (form.name || '').trim();
  const phone = (form.phone || '').trim();
  const email = (form.email || '').trim();
  return { name, phone, email, valid: name.length > 0 };
}

/* ───────────────────────── WhatsApp triage parity (Part 3) ─────────────────────────
 *
 * The WhatsApp router writes an unknown sender's triage holding doc to
 * `draft_contacts/{canonicalPhone}` (channel:'whatsapp_cloud', status:'triage')
 * — the SAME `draft_contacts` collection Email triage uses — keyed by the
 * canonical (digits-only, India-aware) phone, which is also the `triage:<id>`
 * conversation key. These PURE helpers recover that doc id + synthesise a
 * DraftContact-shaped object so the WA triage actions reuse the EXACT Email
 * write path (createLeadFromTriage / createContactFromTriage / setTriageAction).
 * Kept here (pure, no Firebase) so they are unit-tested with the Node runner;
 * the impure writes that consume them live in lib/triageStore.ts.
 * ──────────────────────────────────────────────────────────────────────────────── */

/** Minimal DraftContact shape the WA triage builder returns (pure-testable). */
export interface WaTriageDraft {
  id: string;
  channel: 'whatsapp_cloud';
  status: 'triage';
  firstMessage: string;
  aiLabel: 'unknown_intent';
  aiConfidence: 'low';
  autoArchived: false;
  createdAt: null;
  phone: string;
  canonicalPhone: string;
  knownContactId: null;
}

/**
 * PURE — canonical digits-only India-aware phone key. MUST mirror
 * `canonicalPhone` in functions/lib/waAdminCommand.js (the router's draft id)
 * so the doc we touch is the SAME one the WA router wrote.
 */
export function canonicalWaPhone(s: string): string {
  const d = String(s || '').replace(/[^0-9]/g, '');
  if (!d) return '';
  if (d.length === 10) return `91${d}`;
  if (d.length === 12 && d.startsWith('91')) return d;
  if (d.length === 13 && d.startsWith('091')) return d.slice(1);
  return d;
}

/**
 * PURE — reasonable Firestore-stored phone variants for an equality sweep
 * (used to find the WA router's holding lead, which stores the raw `from`).
 * Mirrors `phoneVariants` in functions/lib/waAdminCommand.js.
 */
export function waPhoneVariants(s: string): string[] {
  const c = canonicalWaPhone(s);
  if (!c) return [];
  const out = new Set<string>([c, `+${c}`]);
  if (c.length === 12 && c.startsWith('91')) {
    const local = c.slice(2);
    out.add(local);
    out.add(`+91${local}`);
    out.add(`91${local}`);
  }
  return [...out];
}

/**
 * PURE — recover the canonical WA triage draft id from a conversation.
 * The triage conversation key is `triage:<draftId>` (draftId = canonical
 * phone). Prefer the key (it is exactly the router's doc id); fall back to
 * canonicalising the phone for any non-`triage:`-keyed row.
 */
export function waTriageDraftId(
  conversationKey: string | null | undefined,
  phone: string | null | undefined,
): string {
  const k = String(conversationKey || '');
  if (k.startsWith('triage:')) {
    const id = k.slice('triage:'.length).trim();
    if (id) return id;
  }
  return canonicalWaPhone(phone || '');
}

/**
 * PURE — hydrate a DraftContact-shaped object for a WhatsApp triage item from
 * the open conversation. createLeadFromTriage / createContactFromTriage read
 * only channel/phone/firstMessage/id/knownContactId, so a minimal synthesised
 * draft is sufficient — and the `id` is the SAME canonical-phone doc the WA
 * router already wrote (the action's setTriageAction then resolves THAT
 * canonical record, Email parity).
 */
export function buildWaTriageDraft(o: {
  draftId: string;
  phone: string;
  firstMessage?: string;
}): WaTriageDraft {
  return {
    id: o.draftId,
    channel: 'whatsapp_cloud',
    status: 'triage',
    firstMessage: o.firstMessage || '',
    aiLabel: 'unknown_intent',
    aiConfidence: 'low',
    autoArchived: false,
    createdAt: null,
    phone: o.phone || '',
    canonicalPhone: o.draftId,
    knownContactId: null,
  };
}

/* ───────────────────────── link-merge body builders ───────────────────────── */

export interface EmailEntry {
  address: string;
  label?: string;
}
export interface PhoneEntry {
  number: string;
  label?: string;
  whatsApp?: boolean;
}

/**
 * PURE — the merge patch that links an EMAIL identity onto an existing contact.
 *
 * `existing` is the contact's current state (top-level `email` + `emails[]` +
 * `linked_google_emails[]`). Returns ONLY the fields that need to change — an
 * empty object means the email was already on the contact (caller can no-op).
 *
 *  - top-level `email` is set ONLY when it is currently blank (never overwrite
 *    an existing primary — this is a merge).
 *  - `linked_google_emails` ALWAYS gains the address (this is the array the
 *    server `routeInboundEmail` falls back to) — deduped, append-only.
 *  - `emails[]` (Person-Hub) gains a `{ address, label:'inbound' }` entry when
 *    absent — deduped by normalised address.
 */
export function linkEmailToContactBody(
  existing: {
    email?: string;
    emails?: EmailEntry[];
    linked_google_emails?: string[];
  },
  senderEmail: string,
): Record<string, unknown> {
  const addr = (senderEmail || '').trim();
  if (!addr) return {};
  const norm = normEmail(addr);
  const patch: Record<string, unknown> = {};

  // top-level primary — only when blank (merge, not overwrite)
  if (!existing.email || !existing.email.trim()) {
    patch.email = addr;
  }

  // linked_google_emails[] — the server's array-contains fallback lane
  const linked = Array.isArray(existing.linked_google_emails)
    ? existing.linked_google_emails
    : [];
  if (!linked.some((e) => normEmail(e) === norm)) {
    patch.linked_google_emails = [...linked, addr];
  }

  // emails[] — Person-Hub UI array
  const emails = Array.isArray(existing.emails) ? existing.emails : [];
  if (!emails.some((e) => normEmail(e.address) === norm)) {
    patch.emails = [...emails, { address: addr, label: 'inbound' }];
  }

  return patch;
}

/**
 * PURE — the merge patch that links a WHATSAPP identity onto an existing
 * contact. Returns ONLY changed fields ({} ⇒ already linked).
 *
 *  - top-level `phone` is set ONLY when blank (the server `resolveKnownClient`
 *    matches on top-level `phone` via phoneVariants — so a blank one MUST be
 *    filled for future inbound to auto-route).
 *  - `phones[]` gains a `{ number, label:'whatsapp', whatsApp:true }` entry
 *    when absent — deduped by normalised number; the whatsApp:true flag is what
 *    the client-side resolveContactByWhatsApp keys on.
 */
export function linkWhatsAppToContactBody(
  existing: { phone?: string; phones?: PhoneEntry[] },
  phone: string,
): Record<string, unknown> {
  const num = (phone || '').trim();
  if (!num) return {};
  const norm = normPhone(num);
  const patch: Record<string, unknown> = {};

  if (!existing.phone || !existing.phone.trim()) {
    patch.phone = num;
  }

  const phones = Array.isArray(existing.phones) ? existing.phones : [];
  if (!phones.some((p) => normPhone(p.number) === norm)) {
    patch.phones = [
      ...phones,
      { number: num, label: 'whatsapp', whatsApp: true },
    ];
  }

  return patch;
}
